import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useLocation } from "wouter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, ChevronDown, Check, FileText, X } from "lucide-react";
import { useTranslations } from "@/lib/translations";
import LeadFormModal, { type LeadContextItem } from "@/components/LeadFormModal";
import { useBookDemo } from "@/components/BookDemoProvider";
import sampleReportImage from "@assets/optimized/esg-sample-report-1280.webp";
import companiesData from "@/data/companies.json";

interface CompanyEntry {
  Name: string;
  "Short Name": string;
  Country: string;
  Sector: string;
}

const allCompanies: CompanyEntry[] = companiesData as CompanyEntry[];
const allCountriesList = [...new Set(allCompanies.map((c) => c.Country))].sort();
const allSectorsList = [...new Set(allCompanies.map((c) => c.Sector))].sort();

const aumOptions = [
  { value: "below100m", labelKey: "sub.aum.below100m" },
  { value: "above100m", labelKey: "sub.aum.above100m" },
];

const groupOptions = [
  { value: "all", labelKey: "sub.market.all" },
  { value: "gcc", labelKey: "sub.market.gcc" },
];

const countryOptions = [
  { value: "saudi", labelKey: "sub.market.saudi" },
  { value: "adx", labelKey: "sub.market.adx" },
  { value: "dfm", labelKey: "sub.market.dfm" },
  { value: "kuwait", labelKey: "sub.market.kuwait" },
  { value: "jordan", labelKey: "sub.market.jordan" },
  { value: "qatar", labelKey: "sub.market.qatar" },
  { value: "palestine", labelKey: "sub.market.palestine" },
  { value: "bahrain", labelKey: "sub.market.bahrain" },
  { value: "oman", labelKey: "sub.market.oman" },
  { value: "lebanon", labelKey: "sub.market.lebanon" },
];

const gccCountries = ["saudi", "adx", "dfm", "kuwait", "qatar", "bahrain", "oman"];
const allCountries = countryOptions.map((c) => c.value);

const marketPricing: Record<string, { below100m: number; above100m: number }> = {
  saudi:     { below100m: 15000, above100m: 20000 },
  adx:       { below100m: 7500,  above100m: 10000 },
  dfm:       { below100m: 5000,  above100m: 7000  },
  kuwait:    { below100m: 10000, above100m: 13000 },
  jordan:    { below100m: 4000,  above100m: 5500  },
  qatar:     { below100m: 3000,  above100m: 4000  },
  palestine: { below100m: 2500,  above100m: 3500  },
  bahrain:   { below100m: 2300,  above100m: 3200  },
  oman:      { below100m: 1500,  above100m: 2000  },
  lebanon:   { below100m: 500,   above100m: 700   },
};

const bundlePricing = {
  gcc: { below100m: 20000, above100m: 30000 },
  all: { below100m: 25000, above100m: 38000 },
};

function calculatePrice(selected: string[], aum: string): number {
  if (selected.length === 0) return 0;

  const tier = aum === "above100m" ? "above100m" : "below100m";

  const isAllSelected = allCountries.every((c) => selected.includes(c));
  if (isAllSelected) return bundlePricing.all[tier];

  const isGccOnly =
    selected.length === gccCountries.length &&
    gccCountries.every((c) => selected.includes(c));
  if (isGccOnly) return bundlePricing.gcc[tier];

  return selected.reduce((total, country) => {
    return total + (marketPricing[country]?.[tier] ?? 0);
  }, 0);
}

function formatPrice(amount: number): string {
  return "$" + amount.toLocaleString("en-US");
}

function AumSelect({
  selected,
  onChange,
}: {
  selected: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslations();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedLabel = selected
    ? t(aumOptions.find((o) => o.value === selected)?.labelKey ?? "")
    : t("sub.selectAum");

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        data-testid="select-aum"
      >
        <span className="truncate text-foreground">{selectedLabel}</span>
        <ChevronDown className={`h-4 w-4 opacity-50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md">
          {aumOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => { onChange(option.value); setOpen(false); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-muted cursor-pointer"
              data-testid={`option-aum-${option.value}`}
            >
              <div className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${selected === option.value ? "bg-accent border-accent" : "border-input"}`}>
                {selected === option.value && <Check className="h-3 w-3 text-white" />}
              </div>
              <span>{t(option.labelKey)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MultiMarketSelect({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const { t } = useTranslations();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isAllSelected = allCountries.every((c) => selected.includes(c));
  const isGccSelected = gccCountries.every((c) => selected.includes(c));

  const handleGroupToggle = (group: string) => {
    if (group === "all") {
      onChange(isAllSelected ? [] : [...allCountries]);
    } else if (group === "gcc") {
      if (isGccSelected) {
        onChange(selected.filter((s) => !gccCountries.includes(s)));
      } else {
        onChange(Array.from(new Set([...selected, ...gccCountries])));
      }
    }
  };

  const handleCountryToggle = (country: string) => {
    if (selected.includes(country)) {
      onChange(selected.filter((s) => s !== country));
    } else {
      onChange([...selected, country]);
    }
  };

  const getSummary = () => {
    if (selected.length === 0) return t("sub.selectMarket");
    if (isAllSelected) return t("sub.market.all");
    if (isGccSelected && selected.length === gccCountries.length) return t("sub.market.gcc");
    if (selected.length === 1) {
      const opt = countryOptions.find((o) => o.value === selected[0]);
      return opt ? t(opt.labelKey) : selected[0];
    }
    return `${selected.length} ${t("sub.marketsSelected")}`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        data-testid="select-market"
      >
        <span className="truncate text-foreground">{getSummary()}</span>
        <ChevronDown className={`h-4 w-4 opacity-50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md max-h-64 overflow-y-auto">
          {groupOptions.map((option) => {
            const isChecked = option.value === "all" ? isAllSelected : isGccSelected;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleGroupToggle(option.value)}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-muted cursor-pointer"
                data-testid={`option-market-${option.value}`}
              >
                <div className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${isChecked ? "bg-accent border-accent" : "border-input"}`}>
                  {isChecked && <Check className="h-3 w-3 text-white" />}
                </div>
                <span className="font-medium">{t(option.labelKey)}</span>
              </button>
            );
          })}
          <div className="border-t border-border" />
          {countryOptions.map((option) => {
            const isChecked = selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleCountryToggle(option.value)}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-muted cursor-pointer"
                data-testid={`option-market-${option.value}`}
              >
                <div className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${isChecked ? "bg-accent border-accent" : "border-input"}`}>
                  {isChecked && <Check className="h-3 w-3 text-white" />}
                </div>
                <span>{t(option.labelKey)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GenericMultiSelect({
  options,
  selected,
  onChange,
  placeholder,
  testId,
}: {
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = options.filter((o) =>
    o.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const getSummary = () => {
    if (selected.length === 0) return placeholder;
    if (selected.length === 1) return selected[0];
    return `${selected.length} selected`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        data-testid={testId}
      >
        <span className="truncate text-foreground">{getSummary()}</span>
        <ChevronDown className={`h-4 w-4 opacity-50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md max-h-64 overflow-y-auto">
          <div className="p-2 border-b border-border">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1 text-sm border border-input rounded bg-background focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          {filtered.map((option) => {
            const isChecked = selected.includes(option);
            return (
              <button
                key={option}
                type="button"
                onClick={() => handleToggle(option)}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-muted cursor-pointer"
              >
                <div className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${isChecked ? "bg-accent border-accent" : "border-input"}`}>
                  {isChecked && <Check className="h-3 w-3 text-white" />}
                </div>
                <span>{option}</span>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">No results</p>
          )}
        </div>
      )}
    </div>
  );
}

const features = [
  "sub.feature.companiesAll",
  "sub.feature.fullRatings",
  "sub.feature.countryFilter",
  "sub.feature.portfoliosUnlimited",
  "sub.feature.benchmarking",
  "sub.feature.reportsUnlimited",
  "sub.feature.export",
  "sub.feature.kpis",
];

export default function SubscriptionCards() {
  const { t } = useTranslations();
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [selectedAum, setSelectedAum] = useState<string>("");
  const [showSampleReport, setShowSampleReport] = useState(false);
  const [indCountries, setIndCountries] = useState<string[]>([]);
  const [indSectors, setIndSectors] = useState<string[]>([]);
  const [indCompanies, setIndCompanies] = useState<string[]>([]);

  const filteredCompanies = allCompanies
    .filter((c) => indCountries.length === 0 || indCountries.includes(c.Country))
    .filter((c) => indSectors.length === 0 || indSectors.includes(c.Sector))
    .map((c) => c.Name)
    .sort();

  const openSampleReport = useCallback(() => {
    setShowSampleReport(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeSampleReport = useCallback(() => {
    setShowSampleReport(false);
    document.body.style.overflow = '';
  }, []);

  useEffect(() => {
    if (!showSampleReport) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeSampleReport(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [showSampleReport, closeSampleReport]);

  const price = calculatePrice(selectedMarkets, selectedAum);
  const displayPrice = price === 0 ? "$0" : formatPrice(price);
  const isGccBundle = selectedMarkets.length === gccCountries.length && gccCountries.every((c) => selectedMarkets.includes(c));
  const isAllBundle = allCountries.every((c) => selectedMarkets.includes(c));
  const hasDiscount = isGccBundle || isAllBundle;

  const [, navigate] = useLocation();
  const { setContext } = useBookDemo();
  const [indModalOpen, setIndModalOpen] = useState(false);

  const proContext = useMemo(() => {
    const marketNames = selectedMarkets.map((val) => {
      const opt = countryOptions.find((o) => o.value === val);
      return opt ? t(opt.labelKey) : val;
    });
    const aumLabel = selectedAum
      ? t(aumOptions.find((o) => o.value === selectedAum)?.labelKey ?? "")
      : t("lead.notSpecified");
    const marketList =
      selectedMarkets.length === 0 ? t("lead.noneSelected") : marketNames.join(", ");
    const items: LeadContextItem[] = [
      { label: t("sub.aumLabel"), value: aumLabel },
      { label: t("sub.selectMarket"), value: marketList },
      { label: t("lead.estimatedFee"), value: `${displayPrice} / ${t("sub.perYear")}` },
    ];
    const payload: Record<string, unknown> = {
      plan: "professional",
      aum: selectedAum ?? null,
      markets: selectedMarkets,
      estimatedAnnualFee: price,
      bundleDiscount: hasDiscount,
    };
    return { items, payload };
  }, [selectedMarkets, selectedAum, displayPrice, price, hasDiscount, t]);

  const indContext = useMemo(() => {
    const reportCount = indCompanies.length > 0 ? indCompanies.length : 1;
    const totalPrice = `$${(reportCount * 100).toLocaleString()}`;
    const items: LeadContextItem[] = [
      {
        label: t("sub.individual.selectCountry"),
        value: indCountries.length > 0 ? indCountries.join(", ") : t("lead.notSpecified"),
      },
      {
        label: t("sub.individual.selectSector"),
        value: indSectors.length > 0 ? indSectors.join(", ") : t("lead.notSpecified"),
      },
      {
        label: t("sub.individual.selectCompany"),
        value: indCompanies.length > 0 ? indCompanies.join(", ") : t("lead.notSpecified"),
      },
      { label: t("lead.estimatedTotal"), value: totalPrice },
    ];
    const payload: Record<string, unknown> = {
      plan: "individual_reports",
      countries: indCountries,
      sectors: indSectors,
      companies: indCompanies,
      reportCount,
      estimatedTotalUsd: reportCount * 100,
    };
    return { items, payload };
  }, [indCountries, indSectors, indCompanies, t]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">

        <Card className="w-full p-8 border-0 shadow-lg flex flex-col" data-testid="card-free">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-foreground mb-1" data-testid="text-free-heading">
              {t('sub.trial.heading')}
            </h3>
            <p className="text-sm italic text-muted-foreground" data-testid="text-free-desc">
              {t('sub.trial.desc')}
            </p>
          </div>

          <ul className="space-y-3 mb-6 flex-1">
            {features.map((featureKey, idx) => (
              <li
                key={idx}
                className="flex items-center gap-3"
                data-testid={`text-free-feature-${idx}`}
              >
                <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                <span className="text-sm text-foreground">{t(featureKey)} <span className="text-xs text-muted-foreground italic">({t('sub.trial.limitedAccess')})</span></span>
              </li>
            ))}
          </ul>

          <button
            onClick={openSampleReport}
            className="flex items-center justify-center gap-2 text-sm text-accent hover:underline mb-4 cursor-pointer"
            data-testid="link-sample-report-free"
          >
            <FileText className="h-4 w-4" />
            {t('sub.trial.viewSample')}
          </button>

          <Button
            variant="accent"
            size="lg"
            className="w-full"
            asChild
            data-testid="button-contact-sales-free"
          >
            <a href="https://platform.esginvest.com/register" target="_blank" rel="noopener noreferrer">
              {t('sub.trial.register')}
            </a>
          </Button>
        </Card>

        <Card className="w-full p-8 border-0 shadow-lg flex flex-col" data-testid="card-subscription">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-foreground mb-1" data-testid="text-subscription-heading">
              {t('sub.pro.heading')}
            </h3>
            <p className="text-sm italic text-muted-foreground" data-testid="text-subscription-desc">
              {t('sub.pro.desc')}
            </p>
          </div>

          <div className="space-y-4 mb-6">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                {t("sub.aumLabel")}
              </label>
              <AumSelect selected={selectedAum} onChange={setSelectedAum} />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                {t("sub.selectMarket")}
              </label>
              <MultiMarketSelect
                selected={selectedMarkets}
                onChange={setSelectedMarkets}
              />
            </div>
          </div>

          <ul className="space-y-3 mb-6 flex-1">
            {features.map((featureKey, idx) => (
              <li
                key={idx}
                className="flex items-center gap-3"
                data-testid={`text-feature-${idx}`}
              >
                <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                <span className="text-sm text-foreground">{t(featureKey)}</span>
              </li>
            ))}
          </ul>

          <div className="text-center mb-6 pt-4 border-t border-border">
            {selectedMarkets.length > 0 && selectedAum ? (
              <>
                <span className="text-4xl font-bold text-foreground" data-testid="text-subscription-price">
                  {displayPrice}
                </span>
                <p className="text-xs text-muted-foreground mt-1">{t("sub.pro.perYear")}</p>
                {hasDiscount && (
                  <p className="text-xs text-accent font-medium mt-1">{t("sub.pro.bundleDiscount")}</p>
                )}
              </>
            ) : (
              <>
                <span className="text-2xl font-bold text-foreground" data-testid="text-subscription-price">
                  {t("sub.pro.customPricing")}
                </span>
                <p className="text-xs text-muted-foreground mt-1">{t("sub.pro.pricingNote")}</p>
              </>
            )}
          </div>

          <button
            onClick={openSampleReport}
            className="flex items-center justify-center gap-2 text-sm text-accent hover:underline mb-4 cursor-pointer"
            data-testid="link-sample-report-pro"
          >
            <FileText className="h-4 w-4" />
            {t('sub.pro.viewSample')}
          </button>

          <Button
            variant="accent"
            size="lg"
            className="w-full"
            data-testid="button-contact-sales"
            onClick={() => {
              setContext(proContext.items, proContext.payload);
              navigate("/book-demo");
            }}
          >
            {t("sub.contactSales")}
          </Button>
        </Card>

        <Card className="w-full p-8 border-0 shadow-lg flex flex-col" data-testid="card-individual-report">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-foreground mb-1" data-testid="text-individual-heading">
              {t('sub.individual.heading')}
            </h3>
            <p className="text-sm italic text-muted-foreground" data-testid="text-individual-desc">
              {t('sub.individual.desc')}
            </p>
          </div>

          <div className="space-y-4 mb-6">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                {t("sub.individual.selectCountry")}
              </label>
              <GenericMultiSelect
                options={allCountriesList}
                selected={indCountries}
                onChange={(vals) => {
                  setIndCountries(vals);
                  setIndCompanies((prev) => prev.filter((name) => {
                    const co = allCompanies.find((c) => c.Name === name);
                    return co && (vals.length === 0 || vals.includes(co.Country));
                  }));
                }}
                placeholder={t("sub.individual.allCountries")}
                testId="select-ind-country"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                {t("sub.individual.selectSector")}
              </label>
              <GenericMultiSelect
                options={allSectorsList}
                selected={indSectors}
                onChange={(vals) => {
                  setIndSectors(vals);
                  setIndCompanies((prev) => prev.filter((name) => {
                    const co = allCompanies.find((c) => c.Name === name);
                    return co && (vals.length === 0 || vals.includes(co.Sector));
                  }));
                }}
                placeholder={t("sub.individual.allSectors")}
                testId="select-ind-sector"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                {t("sub.individual.selectCompany")}
              </label>
              <GenericMultiSelect
                options={filteredCompanies}
                selected={indCompanies}
                onChange={setIndCompanies}
                placeholder={t("sub.individual.allCompanies")}
                testId="select-ind-company"
              />
            </div>
          </div>

          <ul className="space-y-3 mb-6 flex-1">
            {[
              'sub.individual.feature1',
              'sub.individual.feature2',
              'sub.individual.feature3',
              'sub.individual.feature4',
              'sub.individual.feature5',
              'sub.individual.feature6',
            ].map((key, idx) => (
              <li
                key={idx}
                className="flex items-center gap-3"
                data-testid={`text-individual-feature-${idx}`}
              >
                <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                <span className="text-sm text-foreground">{t(key)}</span>
              </li>
            ))}
          </ul>

          <div className="text-center mb-6 pt-4 border-t border-border">
            <span className="text-4xl font-bold text-foreground" data-testid="text-individual-price">
              ${indCompanies.length > 0 ? (indCompanies.length * 100).toLocaleString() : "100"}
            </span>
            <p className="text-xs text-muted-foreground mt-1">
              {indCompanies.length > 1
                ? `${indCompanies.length} ${t('sub.individual.reports')} × $100`
                : t('sub.individual.perReport')}
            </p>
          </div>

          <button
            onClick={openSampleReport}
            className="flex items-center justify-center gap-2 text-sm text-accent hover:underline mb-4 cursor-pointer"
            data-testid="link-sample-report"
          >
            <FileText className="h-4 w-4" />
            {t('sub.individual.viewSample')}
          </button>

          <Button
            variant="accent"
            size="lg"
            className="w-full"
            data-testid="button-request-report"
            onClick={() => setIndModalOpen(true)}
          >
            {t('sub.individual.requestReport')}
          </Button>
        </Card>

      </div>

      <LeadFormModal
        open={indModalOpen}
        onOpenChange={setIndModalOpen}
        source="individual_purchase_report"
        title={t("lead.individual.title")}
        description={t("lead.individual.desc")}
        contextItems={indContext.items}
        contextPayload={indContext.payload}
      />

      {showSampleReport && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={closeSampleReport}
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white z-[10000]"
            onClick={closeSampleReport}
            aria-label="Close"
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={sampleReportImage}
            alt="Sample ESG Report"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
